import { escapeHtml, printDocument } from '#admin/lib/print-frame.ts'
import { qrSvg } from '@ems/ui/lib/qr'

/** What a badge shows. The name is the badge name when the person gave one. */
export type Badge = {
  name: string
  /** The full name under a shorter badge name. Null when they are the same. */
  fullName: string | null
  ticketType: string | null
  code: string
  eventName: string
}

/** The badge to print for a ticket: its badge name in large type, and its full name under it. */
export function badgeOf(
  ticket: {
    holderName: string
    badgeName: string | null
    ticketTypeName: string | null
    code: string
  },
  eventName: string,
): Badge {
  const short = ticket.badgeName?.trim() ?? ''
  const name = short === '' ? ticket.holderName : short
  return {
    name,
    fullName: name === ticket.holderName ? null : ticket.holderName,
    ticketType: ticket.ticketTypeName,
    code: ticket.code,
    eventName,
  }
}

/** One badge as a page of the print document. */
function badgePage(badge: Badge): string {
  const text = escapeHtml
  return `<section class="badge-page">
<div class="event">${text(badge.eventName)}</div>
<div class="who"><div class="name">${text(badge.name)}</div>${
    badge.fullName === null ? '' : `<div class="full">${text(badge.fullName)}</div>`
  }</div>
${badge.ticketType === null ? '' : `<div class="type">${text(badge.ticketType)}</div>`}
<div class="qr">${qrSvg(badge.code, 42)}<div class="code">${text(badge.code)}</div></div>
</section>`
}

/**
 * Badges, 100 × 150 mm each, as one document for the print frame: one badge to a page, the event,
 * the name in large type, the ticket type on a band, and the QR the doors scan.
 *
 * - Each badge breaks to a new page, and the last one does not, so no blank page comes out last.
 * - `print-color-adjust: exact` keeps the bands' colors, which a browser leaves out of a print by
 *   default.
 * - Every text is escaped: the names are typed by the people on the badges.
 */
export function badgesHtml(badges: readonly Badge[]): string {
  const title = badges.length === 1 ? `Badge ${badges[0]?.code ?? ''}` : `${badges.length} badges`
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
@page { size: 100mm 150mm; margin: 0; }
* { box-sizing: border-box; margin: 0; }
body { font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; color: #0f172a;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
@media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
.badge-page { width: 100mm; height: 150mm; display: flex; flex-direction: column; overflow: hidden;
  break-after: page; page-break-after: always; }
.badge-page:last-child { break-after: auto; page-break-after: auto; }
.event { background: #0f172a; color: #fff; padding: 7mm 6mm; font-size: 13pt; font-weight: 700;
  text-align: center; letter-spacing: 0.02em; }
.who { flex: 1; display: flex; flex-direction: column; justify-content: center; align-items: center;
  gap: 2mm; padding: 4mm 6mm; text-align: center; }
.name { font-size: 28pt; font-weight: 800; line-height: 1.05; overflow-wrap: anywhere; }
.full { font-size: 12pt; color: #334155; }
.type { background: #2563eb; color: #fff; padding: 4mm 6mm; font-size: 14pt; font-weight: 700;
  text-align: center; text-transform: uppercase; letter-spacing: 0.06em; }
.qr { display: flex; flex-direction: column; align-items: center; gap: 1.5mm; padding: 5mm 0 6mm; }
.code { font-family: ui-monospace, 'SFMono-Regular', Consolas, monospace; font-size: 10pt; }
</style></head>
<body>
${badges.map(badgePage).join('')}
</body></html>`
}

export const badgeHtml = (badge: Badge): string => badgesHtml([badge])

export function printBadge(badge: Badge): void {
  printDocument(badgeHtml(badge))
}

/** Every badge in one print job: a group, a registration, or the people a search found. */
export function printBadges(badges: readonly Badge[]): void {
  if (badges.length === 0) return
  printDocument(badgesHtml(badges))
}
